import { nullable } from '@ipfabric/utils/fp/nullable';
import { useEffect } from 'react';
import type { FC } from 'react';
import './styles.css';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { ScrollColumnView } from '@ipfabric/ui-components/ScrollView/ScrollColumnView';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { type Point, type Security, point } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import { Pagination } from 'components/Pagination/Pagination';
import { SecuritySchemaTree } from 'components/SecuritySchema/SecuritySchemaTree';
import { makeScrollbarsViewRef } from 'components/SecuritySchema/helpers/makeScrollbarsViewRef';
import {
  RULE_CHAIN_ITEMS_PER_PAGE,
  useSecuritySchemaContextSelectors,
} from 'components/SecuritySchema/model/SecuritySchemaModel';
import { changeRuleChainPage } from 'components/SecuritySchema/model/commands/changeRuleChainPage';
import { selectActiveRuleChain } from 'components/SecuritySchema/model/selectors/selectActiveRuleChain';
import { selectHighlightedRuleIndex } from 'components/SecuritySchema/model/selectors/selectHighlightedRuleIndex';
import { selectRuleChainScope } from 'components/SecuritySchema/model/selectors/selectRuleChainScope';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { flow, pipe } from 'fp-ts/lib/function';
import { useDeferredChange } from 'utils/hooks/useDeferredChange';
import { selectHighlightedRuleOnCurrentPage } from './model/selectors/selectHighlightedRuleOnCurrentPage';
import { selectPaginatedHighlightedRuleIndex } from './model/selectors/selectPaginatedHighlightedRuleIndex';
import { selectPaginatedRuleChainSchemaRoots } from './model/selectors/selectPaginatedRuleChainSchemaRoots';
import { selectSecuritySchemaTreeContext } from './model/selectors/selectSecuritySchemaTreeContext';

/**
 * Optimal distance top of scrolling container to a rule we scrolled onto
 */
const SCROLL_TO_RULE_MARGIN = 20;

const DISPLAYED_PAGES = 11;

const scrollToCoordinates =
  (treeScrollContainer: HTMLDivElement) => (coordinates: Point) => {
    treeScrollContainer.scrollLeft = coordinates.x;
    treeScrollContainer.scrollTop = coordinates.y;
  };

const getRuleNode =
  (treeScrollContainer: HTMLElement) =>
  (ruleIndex: number): HTMLElement | null =>
    treeScrollContainer.querySelector<HTMLElement>(
      // Not the tree path: a cloud rule sits a level deeper, in its policy box.
      `[data-security-rule-index="${ruleIndex}"]`,
    );

const getRuleNodeScrollOffset =
  (treeScrollContainer: HTMLElement) =>
  (nodeElement: HTMLElement): Point =>
    point.create(
      0,
      treeScrollContainer.scrollTop +
        nodeElement.getBoundingClientRect().top -
        treeScrollContainer.getBoundingClientRect().top -
        SCROLL_TO_RULE_MARGIN,
    );

const getRuleCoordinates = (
  container: HTMLElement,
): ((ruleIndex: number | null) => Point | null) =>
  flow(
    nullable.chain(getRuleNode(container)),
    nullable.chain(getRuleNodeScrollOffset(container)),
  );

export const RuleChainDetail: FC = () => {
  const msg = useLocale();

  const bareCtxProps = useSecuritySchemaContextSelectors({
    dispatch: prop('dispatch'),
    ruleChain: selectActiveRuleChain,
    ruleChainSchemaRoots: selectPaginatedRuleChainSchemaRoots,
    ruleChainScope: selectRuleChainScope,
    page: prop('page'),
    highlightedRuleIndex: selectHighlightedRuleIndex,
    highlightedRuleOnCurrentPage: selectHighlightedRuleOnCurrentPage,
    paginatedHighlightedRuleIndex: selectPaginatedHighlightedRuleIndex,
    highlightedRuleLabel: prop('highlightedRuleLabel'),
    securitySchemaTreeContext: selectSecuritySchemaTreeContext,
    treeScrollContainerRef: prop('treeScrollContainerRef'),
  });

  const [ctxProps, schemaRootsChanged] = useDeferredChange(
    bareCtxProps,
    (prev, current) =>
      prev.ruleChainSchemaRoots.roots !== current.ruleChainSchemaRoots.roots,
    {
      applyChangeTimeout: 10, // determined experimentally to show the loader in most cases
    },
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: triggered on chain scope and page loads
  useEffect(() => {
    const treeScrollContainer = ctxProps.treeScrollContainerRef.current;
    if (!treeScrollContainer) {
      return;
    }
    scrollToCoordinates(treeScrollContainer)(
      ctxProps.ruleChainScope?.scrollPosition ??
        getRuleCoordinates(treeScrollContainer)(
          ctxProps.paginatedHighlightedRuleIndex,
        ) ??
        point.zero,
    );
  }, [ctxProps.ruleChainScope, ctxProps.page]);

  const scrollToHighlightedRule = () => {
    if (ctxProps.highlightedRuleIndex === null) {
      stumble('no highlighted rule to scroll to');
      return;
    }
    if (!ctxProps.highlightedRuleOnCurrentPage) {
      ctxProps.dispatch(changeRuleChainPage, {
        page: Math.floor(
          ctxProps.highlightedRuleIndex / RULE_CHAIN_ITEMS_PER_PAGE,
        ),
      });
      return;
    }
    const treeScrollContainer = ctxProps.treeScrollContainerRef.current;
    if (!treeScrollContainer) {
      stumble('Tree scroll container not available, cannot scroll to rule');
      return;
    }
    pipe(
      ctxProps.paginatedHighlightedRuleIndex,
      getRuleCoordinates(treeScrollContainer),
      nullable.chain(scrollToCoordinates(treeScrollContainer)),
    );
  };

  const getRuleLabel = (rules: Security.Rule[], index: number) =>
    index === rules.length
      ? msg.securitySchema.ruleChain.defaultAction
      : rules[index]?.name ||
        msg.securitySchema.rule.labelFromNumber(index + 1);

  const handlePageChange = useStableCallback((page: number) => {
    ctxProps.dispatch(changeRuleChainPage, { page });
  });

  return (
    <div className='mt-lgu-10 flex flex-1 flex-col'>
      {ctxProps.ruleChain === null ||
      ctxProps.ruleChainSchemaRoots.roots === null ? (
        <p className='text-danger mt-lgu-10 text-center'>
          {msg.securitySchema.chainNotFound}
        </p>
      ) : (
        <>
          <h4 className='font-bold'>
            {ctxProps.ruleChain.name}
            <Tooltip content={msg.securitySchema.ruleChain.nameTooltip}>
              <i className='fa fa-fw fa-question-circle ml-lgu-2' />
            </Tooltip>
          </h4>
          {ctxProps.highlightedRuleIndex !== null && (
            <p>
              <strong>
                {ctxProps.highlightedRuleLabel ||
                  msg.securitySchema.ruleChain.defaultHighlightedRuleLabel}
                {': '}
                <ButtonAsA onClick={scrollToHighlightedRule}>
                  {getRuleLabel(
                    ctxProps.ruleChain.rules,
                    ctxProps.highlightedRuleIndex,
                  )}
                </ButtonAsA>
              </strong>
            </p>
          )}
          {ctxProps.ruleChainSchemaRoots.pagination.numberOfPages > 1 && (
            <Pagination
              className={classNames(
                schemaRootsChanged && 'pointer-events-none',
              )}
              {...ctxProps.ruleChainSchemaRoots.pagination}
              displayedPages={DISPLAYED_PAGES}
              onPageChange={handlePageChange}
            />
          )}
          <div className='mt-lgu-10 relative flex flex-1 flex-col'>
            <ScrollColumnView
              ref={makeScrollbarsViewRef(ctxProps.treeScrollContainerRef)}
            >
              <SecuritySchemaTree
                context={ctxProps.securitySchemaTreeContext}
                rootNodes={ctxProps.ruleChainSchemaRoots.roots}
              />
            </ScrollColumnView>
            {schemaRootsChanged && (
              <div className='match-parent background-white-semi'>
                <LoaderAndText />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
